import { config, TWIN_TYPE, nodeSymbolHalfHeight } from './config';
import { zygosityRuns } from './twinLinkage';

export const spousePath = (
  uNode,
  cNode,
  parentRelationship = 'married',
  isConsanguineous = false,
  laneY = null
) => {
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // Gap between the two parallel lines of a consanguineous union. Tied to the
  // symbol size (which scales with zoom) rather than a fixed 15px: the
  // relationship stroke is non-scaling (~constant on screen), so a fixed
  // coordinate-space gap shrinks below the line width when zoomed out and the
  // two lines merge into one. A symbol-proportional gap stays distinguishable
  // at any zoom.
  const line_gap = config.SYMBOL_SIZE * 0.2;

  // STRETCHED route (a decoupled SECONDARY containment bridge whose partners sit in
  // different family columns — G-1-01373's 25-35): drop each partner to a horizontal lane
  // BELOW the row, run the long marriage bar there, and come back up to the other partner.
  // This keeps the bar off the row of nodes it would otherwise run straight over. The
  // shared child drops from the bar (its union node sits ON the lane). divorced/separated
  // ticks are omitted for the stretched form (it is a distant-cousin bar, not a divorce).
  if (laneY != null && Number.isFinite(laneY) && Math.abs(ux - cx) >= 1) {
    let path = `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy}`;
    if (isConsanguineous) {
      const g = line_gap;
      path += ` M${ux},${uy} L${ux},${laneY + g} L${cx},${
        laneY + g
      } L${cx},${cy}`;
    }
    return path;
  }

  let path = `M${ux},${uy} L${cx},${cy}`;
  if (isConsanguineous) {
    path += ` M${ux},${uy - line_gap} L${cx},${cy - line_gap}`;
  }

  const mx = (ux + cx) / 2;
  const my = (uy + cy) / 2;
  const line_height = 32;

  if (parentRelationship === 'divorced') {
    path +=
      ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}` +
      ` M${mx + 40},${my - line_height} L${mx},${my + line_height}`;
  } else if (parentRelationship === 'separated') {
    path += ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}`;
  }

  return path;
};

export const childPath = (uNode, cNode, laneY = null) => {
  let d;
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // offset is set to zero for dev purpose
  const offSet = 0;

  // OVER-THE-TOP route (a displaced sibship whose connector would otherwise sweep an
  // unrelated co-generation): the child sits IN LINE with its generation and the
  // sibling-line runs HIGHER, parallel to and above the generation row, at laneY (above
  // the union). Up from the union, across at laneY, then down to the child.
  if (laneY != null && Math.abs(ux - cx) >= 1) {
    return `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy - offSet}`;
  }

  // Sub-pixel float error from layout arithmetic (e.g. 6600 vs 6599.999999999999)
  // must not turn a genuinely-vertical drop into a dog-leg; a 1px tolerance is far
  // below the symbol-width gap of any real dog-leg.
  if (Math.abs(ux - cx) < 1) {
    // Case 1: same x → straight vertical
    d = `M${ux},${uy} L${ux},${cy - offSet}`;
  } else {
    // Case 2: different x → elbow
    const midY = (uy + cy) / 2;
    d = `M${ux},${uy} 
         L${ux},${midY} 
         L${cx},${midY} 
         L${cx},${cy - offSet}`;
  }

  return d;
};

// Gamete-donor line of descent (Bennett Fig 5): a single STRAIGHT DIAGONAL from the
// bottom of the donor node — NOT the orthogonal elbow of a normal child edge. The donor
// sits on the parents' row and the offspring one row below, so the line reads as a
// diagonal converging on the pregnancy, where the carrier's own vertical descent also
// arrives (Bennett shows several such lines meeting there). `fromNode` is the donor
// (full-size); `toNode` is the offspring (often the smaller pregnancy symbol).
//
// Target depends on whether the donor produced a WHOLE sibship (Bennett 5.1c). With no
// `carrierNode`, aim at the offspring's TOP edge (single offspring, or offspring in
// different sibships → one diagonal each). With a `carrierNode`, the offspring shares a
// sibship with siblings from the same donor: aim instead at the right-angle CORNER of
// the sibship bar — the elbow midpoint (carrier.y + child.y)/2, matching childPath's bar
// — so ONE line feeds the whole sibship rather than one line per child.
// `aimPoint` overrides the target when the offspring's drop does not come off an ordinary sibship
// bar. That is the TWIN case: a twin pregnancy's drop forks at an apex partway down, so a line to
// the sibship-bar corner visibly misses the fork (reported in QA). The caller passes the apex.
// MIDDLE-TO-MIDDLE, like spousePath (TGCLOUD-1577). Tim's QA on G-1-01491: an identified donor's
// line "isn't from the central point on the side of the node … it doesn't line up well with the
// existing relationship line" — node 8 was both node 3's partner and node 1's sperm donor, so a
// marriage bar and a donor line met the same square at two different heights.
//
// The cause was that this line started at the donor's BOTTOM EDGE while every relationship line
// runs centre-to-centre. That is the shape Kathryn already ruled against for spouse bars — a line
// "coming from the bottom of the icon … should never be allowed" — so the fix is to adopt the same
// convention here rather than invent a third one. Starting at the node's centre means the line
// leaves the symbol's SIDE at exactly the marriage bar's height, because the symbol is painted over
// the inner part of the line; the two now meet the node at the same point.
//
// The bar-corner and apex aims are unchanged: those are real geometric targets (where the sibship
// drop turns, where a twin fan forks), not node centres, and they were both QA-driven.
export const reproDescentPath = (
  fromNode,
  toNode,
  carrierNode = null,
  aimPoint = null
) => {
  const x1 = fromNode.x;
  const y1 = fromNode.y; // donor CENTRE — see above
  if (aimPoint && Number.isFinite(aimPoint.x) && Number.isFinite(aimPoint.y)) {
    return `M${x1},${y1} L${aimPoint.x},${aimPoint.y}`;
  }
  const x2 = toNode.x;
  const y2 =
    carrierNode != null
      ? (carrierNode.y + toNode.y) / 2 // sibship-bar right-angle corner (Bennett 5.1c)
      : toNode.y; // offspring CENTRE, for the same reason
  return `M${x1},${y1} L${x2},${y2}`;
};

// Dual-parent adoption (Bennett Fig 3): a SOLID reference connector from an adopted-in child UP
// to its in-graph biological parents. Aims at the midpoint of whichever bio parents are present
// (their marriage-bar level when both are given, the single parent's centre otherwise); the
// child end attaches at its top edge so the line reads as a descent distinct from the dashed
// adoptive line. Shared by the engine (edge emission) and the live drag so the geometry can't
// drift. Straight line — crossing-aware routing is a deferred refinement.
export const bioConnectorPath = (childNode, parentNodes) => {
  const parents = (parentNodes || []).filter(
    (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y)
  );
  if (!childNode || !Number.isFinite(childNode.x) || parents.length === 0) {
    return '';
  }
  const x2 = parents.reduce((s, p) => s + p.x, 0) / parents.length;
  const y2 = parents.reduce((s, p) => s + p.y, 0) / parents.length;
  const x1 = childNode.x;
  const y1 = childNode.y - nodeSymbolHalfHeight(childNode); // child top edge
  return `M${x1},${y1} L${x2},${y2}`;
};

// The APEX of a twin group: the point the parents' drop reaches and from which the slanted lines
// fan out to each twin. Exported because more than one thing must aim at it — the twin path itself
// and a gamete-donor's line of descent, which for a twin pregnancy has to meet the fork rather than
// the ordinary sibship bar. One formula, so the two can never disagree (the same reason unionVX and
// buildRenderModel share their anchor rule).
export const twinApex = (unionNode, twinNodes) => {
  if (!unionNode || !twinNodes || twinNodes.length === 0) return null;
  const xs = twinNodes.map((t) => t.x).filter((x) => Number.isFinite(x));
  if (!xs.length || !Number.isFinite(unionNode.y)) return null;
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const twinY = twinNodes[0].y;
  // ONE height, every size. A size-aware apex was tried and withdrawn: the twin fan's
  // elbow and an ordinary sibling's sibship bar are both drawn off the same vertical drop
  // from the union, and both sit at 0.5 — so moving only the twin one makes a sibship
  // containing a triplet draw TWO horizontal bars. See the config note.
  return {
    x: centerX,
    y: unionNode.y + (twinY - unionNode.y) * config.TWIN_DIAGONAL_MEETING_HEIGHT
  };
};

/**
 * @param unionNode  the parents' union (or single parent) the drop comes from
 * @param twinNodes  the twin set's nodes
 * @param cohortNodes optional — every node drawn in the same SIBSHIP. Glyph adjacency is
 *   judged over this, so an ordinary full sibling seated between two co-multiples breaks
 *   the run instead of being invisible to it. Defaults to the twin set alone, which is
 *   what `MenuIcons` (synthetic two-node fixtures) and any older caller want.
 */
export const twinGroupPath = (unionNode, twinNodes, cohortNodes) => {
  if (!twinNodes || twinNodes.length === 0) {
    return { path: '', unknownMarkers: [] };
  }

  const { x: ux, y: uy } = unionNode;

  // Horizontal centre + meeting point, from the shared helper so the donor line can aim at the
  // identical apex.
  const apex = twinApex(unionNode, twinNodes);
  const centerX = apex.x;
  const twinY = twinNodes[0].y;
  const meetingY = apex.y;

  // bar position
  const barHeightRatio = 0.5;
  const barY = meetingY + (twinY - meetingY) * barHeightRatio;

  let path = '';

  // Elbow line from union node down to meeting point
  // Vertical down from union, then horizontal to center
  path += `M ${ux},${uy} L ${ux},${meetingY} L ${centerX},${meetingY}`;

  // Diagonal lines from meeting point to each twin
  twinNodes.forEach((twin) => {
    path += ` M ${centerX},${meetingY} L ${twin.x},${twin.y}`;
  });

  const unknownMarkers = [];

  // Helper to calculate x position on diagonal line at given y
  const getXOnDiagonal = (twinX, twinY_) => {
    if (twinY_ === meetingY) return twinX; // Avoid division by zero
    return (
      centerX + ((barY - meetingY) * (twinX - centerX)) / (twinY_ - meetingY)
    );
  };

  // === Zygosity glyphs (TGCLOUD-1692) ===
  //
  // ONE GLYPH PER RUN, not per pair. This used to walk every confirmed pair and emit a
  // bar (monozygotic) or a "?" (unknown) for each, all at the same `barY` and with no
  // test of whether the two were actually next to each other. Two consequences, both
  // verified on this renderer rather than reasoned about:
  //
  //   * an unknown-zygosity TRIPLET produced THREE overlapping question marks at
  //     5rem apiece (a quadruplet, six);
  //   * a trizygotic triplet whose identical pair sat at the two OUTER positions drew
  //     one bar straight across the middle sibling's descent line — the chart
  //     asserting that all three were identical, which is a clinical mis-statement
  //     rather than a cosmetic defect.
  //
  // A "run" is a maximal set of members that are both in one zygosity component AND
  // adjacent left-to-right on the chart, so it is exactly the span a single glyph may
  // legitimately cover. The failure mode becomes an OMISSION — a non-adjacent
  // identical pair draws no bar at all — which understates rather than asserts
  // something untrue. `orderSiblings` keeps such a pair adjacent; this is what keeps
  // the drawing honest when it cannot (a locked `establishedOrder`, say).
  //
  // Note the DRAWN order has to be derived here. The array arrives in twin sub-group
  // insertion order (`Family.js` → `getTwinsInSameSubGroup`), which is not x order —
  // so anything reasoning about adjacency has to sort first. The diagonals above are
  // deliberately still emitted in the ORIGINAL order, which keeps a pair's path string
  // byte-identical to what every existing chart already has.
  const positioned = twinNodes.filter((t) => t && Number.isFinite(t.x));
  const byPid = new Map(positioned.map((t) => [String(t.pid), t]));
  // The drawn row this set sits in. Non-members are deliberately INCLUDED: a run breaks
  // on anyone who is not in a zygosity component, so an ordinary sibling between two
  // co-multiples stops a bar being drawn across them. Members are unioned in so a caller
  // that hands over an incomplete cohort cannot silently drop one.
  const cohort = [
    ...positioned,
    ...(Array.isArray(cohortNodes) ? cohortNodes : [])
  ].filter((n) => n && Number.isFinite(n.x));
  const seen = new Set();
  const drawnPids = cohort
    .filter((n) => {
      const key = String(n.pid);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.x - b.x)
    .map((n) => n.pid);

  // The bar x of each member of a run, which is where its diagonal crosses `barY`.
  const runBarXs = (run) =>
    run
      .map((pid) => byPid.get(String(pid)))
      .filter(Boolean)
      .map((t) => getXOnDiagonal(t.x, t.y));

  // Monozygotic: one horizontal bar spanning the run. For a fully identical triplet
  // this is one continuous line across all three, which is the correct Bennett
  // notation — and is what the old per-pair code happened to produce by overlapping
  // three separate segments.
  const monozygoticSpans = [];
  zygosityRuns(positioned, drawnPids, TWIN_TYPE.monozygotic).forEach((run) => {
    const xs = runBarXs(run);
    if (xs.length < 2) return;
    const from = Math.min(...xs);
    const to = Math.max(...xs);
    monozygoticSpans.push([from, to]);
    path += ` M ${from},${barY} L ${to},${barY}`;
  });

  /**
   * Where the "?" for one run goes: IN A GAP between the descent lines, never on one.
   *
   * Bennett puts the mark in the opening between the lines it refers to, and the reason
   * matters practically as well — a glyph on top of a line is unreadable. The first cut
   * placed it at the run's MIDPOINT, which is right for a pair and wrong for anything
   * odd-sized: a symmetric triplet's middle member sits directly below the apex, so its
   * "diagonal" is a vertical line at exactly the run's centre and the mark landed on it.
   * Reported from a real chart, and reproduced at real spacing — descent lines crossing
   * the bar level at 400 / 800 / 1200 with the marker at 800.
   *
   * So: take the widest gap between consecutive lines. Prefer one no monozygotic bar
   * covers, since a mark on a bar is just as obscured. Ties break toward the run's centre
   * and then leftward, so the choice is deterministic and the mark stays near the middle
   * of the set it describes.
   *
   * A PAIR IS UNCHANGED: two lines, one gap, and its midpoint IS the run midpoint.
   */
  const markerXFor = (xs) => {
    const sorted = [...xs].sort((a, b) => a - b);
    const centre = (sorted[0] + sorted[sorted.length - 1]) / 2;
    const gaps = sorted.slice(0, -1).map((from, i) => {
      const to = sorted[i + 1];
      const mid = (from + to) / 2;
      return {
        mid,
        width: to - from,
        onBar: monozygoticSpans.some(([lo, hi]) => mid >= lo && mid <= hi)
      };
    });
    const clear = gaps.filter((g) => !g.onBar);
    const pool = clear.length ? clear : gaps;
    pool.sort(
      (a, b) =>
        b.width - a.width ||
        Math.abs(a.mid - centre) - Math.abs(b.mid - centre) ||
        a.mid - b.mid
    );
    return pool[0].mid;
  };

  // Unknown: one "?" per run. `pids` travels with the marker so the live-drag refresh in
  // Canvas can re-match markers by identity instead of by array index — with two runs in
  // one set, or two twin sub-groups under one union (both of which key on the union id),
  // index matching assigns the wrong position.
  zygosityRuns(positioned, drawnPids, TWIN_TYPE.unknown).forEach((run) => {
    const xs = runBarXs(run);
    if (xs.length < 2) return;
    unknownMarkers.push({ x: markerXFor(xs), y: barY, pids: run });
  });

  return { path, unknownMarkers };
};
